import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';

const SmartPagination = ({
  currentPage,
  totalPages,
  onPageChange,
  totalItems,
  pageSize,
  className = '',
  /** Sunucu tam toplam bilmediğinde (ör. arama): sonuç sayısı alt sınır */
  hasMore = false,
  /** Verilirse sayfa düğmeleri gerçek <a> olur; tarayıcı ?page= adresini izleyebilir. */
  hrefForPage,
}) => {
  const [goToValue, setGoToValue] = useState('');

  if (totalPages <= 1 && !hasMore) return null;

  const handleGoTo = (e) => {
    e.preventDefault();
    const p = parseInt(goToValue);
    if (p >= 1 && p <= totalPages) {
      onPageChange(p);
      setGoToValue('');
    }
  };

  // Generate page numbers with ellipsis
  const getPageNumbers = () => {
    const pages = [];
    const delta = 2; // pages around current

    // Always show first page
    pages.push(1);

    const rangeStart = Math.max(2, currentPage - delta);
    const rangeEnd = Math.min(totalPages - 1, currentPage + delta);

    if (rangeStart > 2) pages.push('...');

    for (let i = rangeStart; i <= rangeEnd; i++) {
      pages.push(i);
    }

    if (rangeEnd < totalPages - 1) pages.push('...');

    // Always show last page
    if (totalPages > 1) pages.push(totalPages);

    return pages;
  };

  const from = (currentPage - 1) * pageSize + 1;
  const to = Math.min(currentPage * pageSize, totalItems);

  const pageButton = (page, children, testId, disabled) => {
    if (disabled || !hrefForPage) {
      return (
        <Button
          variant="outline"
          size="icon"
          className="h-8 w-8"
          disabled={disabled}
          onClick={() => !disabled && onPageChange(page)}
          data-testid={testId}
        >
          {children}
        </Button>
      );
    }
    return (
      <Button variant="outline" size="icon" className="h-8 w-8" asChild>
        <Link
          to={hrefForPage(page)}
          data-testid={testId}
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        >
          {children}
        </Link>
      </Button>
    );
  };

  return (
    <div className={`flex flex-col sm:flex-row items-center justify-between gap-3 mt-6 ${className}`} data-testid="smart-pagination">
      {/* Info */}
      <div className="text-sm text-muted-foreground" data-testid="pagination-info">
        {hasMore ? `${from}-${to}+` : `${from}-${to} / ${totalItems?.toLocaleString()}`}
      </div>

      {/* Page buttons */}
      <div className="flex items-center gap-1 flex-wrap justify-center">
        {/* First */}
        {pageButton(1, <ChevronsLeft className="h-4 w-4" />, 'pagination-first', currentPage === 1)}
        {pageButton(currentPage - 1, <ChevronLeft className="h-4 w-4" />, 'pagination-prev', currentPage === 1)}

        {/* Page numbers */}
        {getPageNumbers().map((page, i) => (
          page === '...' ? (
            <span key={`ellipsis-${i}`} className="px-1 text-muted-foreground">...</span>
          ) : hrefForPage ? (
            <Button
              key={page}
              variant={currentPage === page ? 'default' : 'outline'}
              size="icon"
              className="h-8 w-8 text-xs"
              asChild
            >
              <Link
                to={hrefForPage(page)}
                data-testid={`pagination-page-${page}`}
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              >
                {page}
              </Link>
            </Button>
          ) : (
            <Button
              key={page}
              variant={currentPage === page ? 'default' : 'outline'}
              size="icon"
              className="h-8 w-8 text-xs"
              onClick={() => onPageChange(page)}
              data-testid={`pagination-page-${page}`}
            >
              {page}
            </Button>
          )
        ))}

        {pageButton(currentPage + 1, <ChevronRight className="h-4 w-4" />, 'pagination-next', currentPage === totalPages)}
        {pageButton(totalPages, <ChevronsRight className="h-4 w-4" />, 'pagination-last', currentPage === totalPages)}
      </div>

      {/* Go to page */}
      <form onSubmit={handleGoTo} className="flex items-center gap-1.5">
        <Input
          type="number"
          min={1}
          max={totalPages}
          value={goToValue}
          onChange={(e) => setGoToValue(e.target.value)}
          placeholder={`1-${totalPages}`}
          className="w-20 h-8 text-xs"
          data-testid="pagination-goto-input"
        />
        <Button type="submit" variant="outline" size="sm" className="h-8 text-xs" data-testid="pagination-goto-btn">
          Git
        </Button>
      </form>
    </div>
  );
};

export default SmartPagination;
